import httpClient from "@plugins/http";
import { ApiResponse } from "@apis/api.ts";
import appConfigs from "@configs/app";
import { RubricResponse, RubricRecommendationRequest } from "@modules/grading-rubric/types.ts";
import {
  AAHtml5RecommendationRequestModel,
  AcpStatusResponse,
  AcpResponse,
  AcpImageRequest,
  AcpGamificationRequest,
  AcpGeneratedModule,
} from "@plugins/authoring-copilot/types.ts";
import { GameAwardsData } from "@/modules/editor/types";
import {
  AcpGenericResponse,
  AcpModulePlan,
  AcpActivityComponents,
  GenerateComponentsResponse,
} from "@/modules/editor/authoring-copilot-types";

export default {
  postModuleRecommendation: (requestData): Promise<ApiResponse<AcpGenericResponse>> => {
    return httpClient.post(`/apis/authoring-copilot/module`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  postActivityRecommendation: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/activity`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  postComponentRecommendation: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/component`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  postRegenerateComponentRecommendation: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/component/regenerate`, requestData);
  },
  postRubricRecommendation(requestData: RubricRecommendationRequest): Promise<ApiResponse<void>> {
    return httpClient.post(`/apis/authoring-copilot/rubric`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  getRecommendationStatus: (requestData) => {
    const queryParams = new URLSearchParams();
    queryParams.append("id", requestData.requestId);
    if (requestData.getParentStatus) {
      queryParams.append("getParentStatus", requestData.getParentStatus);
    }
    return httpClient.get(
      `/apis/authoring-copilot/${requestData.appCategory}/status?${queryParams.toString()}`,
    );
  },
  getRecommendation: (requestData): Promise<AcpActivityComponents> => {
    return httpClient.get(`/authoring-copilot/${requestData.requestId}/recommendations.json`, {
      baseURL: `${appConfigs.s3ResourceDomain}`,
      headers: { Accept: "application/json" },
      withCredentials: true,
    });
  },
  getModulePlanRecommendation(requestId: string): Promise<AcpModulePlan> {
    return httpClient.get(`/authoring-copilot/module-plan/${requestId}/recommendations.json`, {
      baseURL: `${appConfigs.s3ResourceDomain}`,
      headers: { Accept: "application/json" },
      withCredentials: true,
    });
  },
  getRubricRecommendation(requestId: string): Promise<ApiResponse<RubricResponse>> {
    return httpClient.get(`/authoring-copilot/${requestId}/rubric.json`, {
      baseURL: `${appConfigs.s3ResourceDomain}`,
      headers: { Accept: "application/json" },
      withCredentials: true,
    });
  },
  getGamificationRecommendation(requestId: string): Promise<GameAwardsData> {
    return httpClient.get(`/authoring-copilot/gamification/${requestId}/recommendations.json`, {
      baseURL: `${appConfigs.s3ResourceDomain}`,
      headers: { Accept: "application/json" },
      withCredentials: true,
    });
  },
  getKnowledgeBases: (requestData) => {
    return httpClient.get(`/apis/authoring-copilot/knowledge-base/list`, {
      params: requestData,
    });
  },
  createKnowledgeBase: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/knowledge-base/create`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  updateKnowledgeBase: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/knowledge-base/update`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  deleteKnowledgeBase: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/knowledge-base/delete?ids=${requestData.ids}`);
  },
  reorderKnowledgeBase(requestData) {
    const formData = new FormData();
    Object.keys(requestData).forEach((key) => {
      formData.append(key, requestData[key]);
    });
    return httpClient.post(`/apis/authoring-copilot/knowledge-base/reorder`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  generateSections: (lessonUuid, requestData) => {
    return httpClient.post(`/apis/authoring-copilot/section/generate/${lessonUuid}`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  generateActivities: (requestData) => {
    return httpClient.post(`/apis/authoring-copilot/activity/generate`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  generateComponents: (requestData): Promise<ApiResponse<GenerateComponentsResponse>> => {
    return httpClient.post(`/apis/authoring-copilot/component/generate`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  regenerateComponents: (activityId, requestData) => {
    return httpClient.post(
      `/apis/authoring-copilot/component/regenerate/${activityId}`,
      requestData,
      {
        headers: {
          "X-With-Richtext": true,
        },
      },
    );
  },
  generateAcpHtml5Interactive(
    requestData: AAHtml5RecommendationRequestModel,
  ): Promise<ApiResponse<AcpResponse>> {
    return httpClient.post(`/apis/authoring-copilot/html5/generate`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  getAcpHtml5InteractiveStatus(params): Promise<ApiResponse<AcpStatusResponse>> {
    return httpClient.get(`/apis/authoring-copilot/html5/status`, {
      params: { requestId: params.requestId },
    });
  },
  generateAcpImage(requestData: AcpImageRequest): Promise<ApiResponse<AcpResponse>> {
    return httpClient.post(`/apis/authoring-copilot/image/generate`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  getAcpImageStatus(params): Promise<ApiResponse<AcpStatusResponse>> {
    return httpClient.get(`/apis/authoring-copilot/image/status`, {
      params: { requestId: params.requestId },
    });
  },
  async generateAcpGamification(
    requestData: AcpGamificationRequest,
  ): Promise<ApiResponse<AcpResponse>> {
    return await httpClient.post(`/apis/authoring-copilot/gamification/generate`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  async getAcpGamificationStatus(params): Promise<ApiResponse<AcpStatusResponse>> {
    const data = new URLSearchParams();
    data.append("requestId", params.requestId);
    return await httpClient.get(`/apis/authoring-copilot/gamification/status?${data}`);
  },
  async generateModule(requestData): Promise<ApiResponse<AcpGeneratedModule>> {
    return await httpClient.post(`/apis/authoring-copilot/module/generate`, requestData, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
};
